import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import AuthLayout from "./AuthLayout";
import { Button, Field, Input, Badge } from "@/components/design-system";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";
import { TID } from "@/lib/testIds";
import { publicReferralInfo, trackReferralClick } from "@/lib/referrals";

const cn = (...xs) => xs.filter(Boolean).join(" ");

function RoleCard({ selected, onClick, title, description, testId }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      className={cn(
        "text-left rounded-2xl border-2 p-5 transition-all",
        selected ? "border-[#2D7FF9] bg-[#E8F1FF]" : "border-[#0B1F3A]/15 hover:border-[#0B1F3A]/30 bg-white"
      )}
    >
      <div className="font-[Montserrat] font-bold text-lg text-[#0B1F3A]">{title}</div>
      <div className="text-sm text-[#0B1F3A]/70 mt-1">{description}</div>
    </button>
  );
}

export default function Register() {
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const { register } = useAuth();
  const initialRole = sp.get("role") === "community" ? "community" : "founder";
  const [role, setRole] = useState(initialRole);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [consentMarketing, setConsentMarketing] = useState(false);
  const [consentTerms, setConsentTerms] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [refInfo, setRefInfo] = useState(null);

  // Cycle C7 — ref code from ?ref= OR persisted from an earlier click
  const refCode = useMemo(() => {
    const q = (sp.get("ref") || "").trim().toLowerCase();
    if (q) return q;
    try {
      return (localStorage.getItem("slll_ref") || "").toLowerCase();
    } catch (_) {
      return "";
    }
  }, [sp]);

  useEffect(() => {
    if (!refCode) return;
    // Persist for cross-tab/late signups (safe: no PII)
    try {
      localStorage.setItem("slll_ref", refCode);
    } catch (_) {}
    // Track the click + resolve display info in parallel — swallow errors quietly
    trackReferralClick(refCode, document.referrer || "").catch(() => {});
    publicReferralInfo(refCode)
      .then((info) => setRefInfo(info))
      .catch(() => setRefInfo(null));
  }, [refCode]);

  const pwStrength = useMemo(() => {
    let s = 0;
    if (password.length >= 8) s++;
    if (/[A-Z]/.test(password)) s++;
    if (/[0-9]/.test(password)) s++;
    if (/[^A-Za-z0-9]/.test(password)) s++;
    return s; // 0..4
  }, [password]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!consentTerms) {
      setError("Please accept the terms and privacy policy to continue.");
      return;
    }
    setBusy(true);
    try {
      await register({
        email, password, name, role,
        consent_marketing: consentMarketing,
        consent_terms: consentTerms,
        ref_code: refCode || undefined,
      });
      // Consume the ref cookie now that we've attached it
      try { localStorage.removeItem("slll_ref"); } catch (_) {}
      nav("/app", { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout
      eyebrow="Create account"
      title="Start your launch loop."
      subtitle="Free forever to give feedback. Founders pay $10/mo when they're ready to launch."
      footer={<>Already have an account? <Link to="/login" className="text-[#2D7FF9] font-semibold">Log in →</Link></>}
    >
      <form onSubmit={onSubmit} className="space-y-5">
        {refInfo && (
          <div
            data-testid="register-referral-banner"
            className="rounded-xl border-2 border-[#7ED957]/40 bg-[#F3FBEF] px-4 py-3 flex items-center gap-3"
          >
            <Badge variant="ambassador">Referred</Badge>
            <p className="text-sm text-[#0B1F3A]">
              You were invited by{" "}
              <span className="font-bold" data-testid="register-referral-name">
                {refInfo.ambassador_name}
              </span>
              . They'll get credit when you verify your email.
            </p>
          </div>
        )}
        <div>
          <p className="text-sm font-semibold text-[#0B1F3A] mb-2">I'm here to…</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <RoleCard
              testId={TID.auth.registerRoleFounder}
              selected={role === "founder"}
              onClick={() => setRole("founder")}
              title="Launch a SaaS"
              description="Validate my product, run campaigns, recruit ambassadors."
            />
            <RoleCard
              testId={TID.auth.registerRoleCommunity}
              selected={role === "community"}
              onClick={() => setRole("community")}
              title="Give feedback & earn"
              description="Review products, build reputation, become a Verified Ambassador."
            />
          </div>
        </div>

        <Field label="Full name" htmlFor="reg-name">
          <Input id="reg-name" data-testid={TID.auth.registerName} value={name} onChange={(e) => setName(e.target.value)} required placeholder="Ada Lovelace" />
        </Field>
        <Field label="Work email" htmlFor="reg-email">
          <Input id="reg-email" data-testid={TID.auth.registerEmail} type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" />
        </Field>
        <Field label="Password" hint="At least 8 characters. Add a number and a symbol for a stronger password." htmlFor="reg-pw">
          <Input id="reg-pw" data-testid={TID.auth.registerPassword} type="password" autoComplete="new-password" minLength={8} required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Choose a strong password" />
          <div className="mt-1.5 flex gap-1">
            {[0,1,2,3].map((i) => (
              <div key={i} className={cn("h-1 flex-1 rounded", pwStrength > i ? (pwStrength >= 3 ? "bg-[#7ED957]" : "bg-[#2D7FF9]") : "bg-[#0B1F3A]/10")} />
            ))}
          </div>
        </Field>

        <div className="space-y-2">
          <label className="flex items-start gap-3 text-sm text-[#0B1F3A]/85 cursor-pointer">
            <input
              data-testid={TID.auth.registerConsentTerms}
              type="checkbox"
              checked={consentTerms}
              onChange={(e) => setConsentTerms(e.target.checked)}
              className="mt-1 w-4 h-4 rounded border-[#0B1F3A]/30 accent-[#2D7FF9]"
            />
            <span>I agree to the <Link to="/legal/terms" className="text-[#2D7FF9] font-semibold">Terms</Link> and <Link to="/legal/privacy" className="text-[#2D7FF9] font-semibold">Privacy Policy</Link> (GDPR).</span>
          </label>
          <label className="flex items-start gap-3 text-sm text-[#0B1F3A]/70 cursor-pointer">
            <input
              data-testid={TID.auth.registerConsentMarketing}
              type="checkbox"
              checked={consentMarketing}
              onChange={(e) => setConsentMarketing(e.target.checked)}
              className="mt-1 w-4 h-4 rounded border-[#0B1F3A]/30 accent-[#2D7FF9]"
            />
            <span>Send me product updates and launch tips (optional).</span>
          </label>
        </div>

        {error && <p data-testid={TID.auth.registerError} className="text-sm font-medium text-red-600">{error}</p>}

        <Button data-testid={TID.auth.registerSubmit} type="submit" variant="primary" className="w-full" disabled={busy}>
          {busy ? "Creating account…" : "Create account"}
        </Button>
      </form>
    </AuthLayout>
  );
}
